'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

// Error messages are i18n keys under `metaConversion.*`. The shape rules mirror
// `fbConversionCredentialsSchema` server-side: both inputs are masked, so a
// swapped paste (token in Pixel ID, pixel in Access Token) is invisible to the
// merchant and silently kills CAPI delivery.
const metaConversionFormSchema = z.object({
  accessToken: z
    .string()
    .trim()
    .min(1, 'accessTokenRequired')
    .refine((value) => !/^\d+$/.test(value), 'accessTokenLooksLikePixelId'),
  pixelId: z
    .string()
    .trim()
    .min(1, 'pixelIdRequired')
    .regex(/^\d{5,20}$/, 'pixelIdMustBeNumeric'),
  publishPurchaseIfNewCustomerOnly: z.boolean(),
})

type FormValues = z.infer<typeof metaConversionFormSchema>

interface MetaConversionFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function MetaConversionForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: MetaConversionFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(metaConversionFormSchema),
    defaultValues: {
      accessToken: defaultValues.credentials?.accessToken ?? '',
      pixelId: defaultValues.credentials?.pixelId ?? '',
      publishPurchaseIfNewCustomerOnly: defaultValues.credentials?.publishPurchaseIfNewCustomerOnly ?? false,
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { accessToken, pixelId, publishPurchaseIfNewCustomerOnly } = data
    await onSubmit({
      credentials: {
        accessToken: touchedFields.accessToken ? accessToken : (defaultValues.credentials?.accessToken ?? ''),
        pixelId: touchedFields.pixelId ? pixelId : (defaultValues.credentials?.pixelId ?? ''),
        publishPurchaseIfNewCustomerOnly: touchedFields.publishPurchaseIfNewCustomerOnly
          ? publishPurchaseIfNewCustomerOnly
          : (defaultValues.credentials?.publishPurchaseIfNewCustomerOnly ?? false),
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('metaConversion.apiCredentials')}</CardTitle>
          <CardDescription>
            {t('metaConversion.apiCredentialsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="accessToken">
              {t('metaConversion.accessToken')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="accessToken"
              type="password"
              placeholder={t('metaConversion.accessTokenPlaceholder')}
              disabled={isLoading}
              {...register('accessToken')}
            />
            {errors.accessToken && (
              <p className="text-xs text-destructive">{t(`metaConversion.${errors.accessToken.message}`)}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="pixelId">
              {t('metaConversion.pixelId')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="pixelId"
              type="password"
              placeholder={t('metaConversion.pixelIdPlaceholder')}
              disabled={isLoading}
              {...register('pixelId')}
            />
            {errors.pixelId && (
              <p className="text-xs text-destructive">{t(`metaConversion.${errors.pixelId.message}`)}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('metaConversion.purchaseEventSettings')}</CardTitle>
          <CardDescription>
            {t('metaConversion.purchaseEventDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="publishPurchaseIfNewCustomerOnly" className="font-medium">
                {t('metaConversion.publishNewCustomersOnly')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('metaConversion.publishNewCustomersHint')}
              </p>
            </div>
            <Switch
              id="publishPurchaseIfNewCustomerOnly"
              checked={watch('publishPurchaseIfNewCustomerOnly')}
              onCheckedChange={(checked) =>
                setValue('publishPurchaseIfNewCustomerOnly', checked, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
